import apiClient from '@/api/client';
import type {
  BarrierDeletionResponse,
  BarrierGeoJsonFeature,
  BarrierListFilters,
  BarrierMutationResponse,
  BarrierIdsResponse,
  BarrierRequest,
  BarrierSortDirection,
  BarrierSortField,
  BarriersListResponse,
  BulkDeleteBarriersResponse,
  SuggestWaypointsRequest,
  SuggestWaypointsResponse,
} from '@/types/barriers';

export interface ListBarriersParams extends BarrierListFilters {
  readonly limit: number;
  readonly offset: number;
  readonly sortBy?: BarrierSortField;
  readonly sortDir?: BarrierSortDirection;
  readonly signal?: AbortSignal;
}

export async function createBarrier(
  body: BarrierRequest,
): Promise<BarrierMutationResponse> {
  const response = await apiClient.post<BarrierMutationResponse>(
    '/api/v1/barriers',
    body,
  );
  return response.data;
}

export async function replaceBarrier(
  id: number,
  body: BarrierRequest,
): Promise<BarrierMutationResponse> {
  const response = await apiClient.put<BarrierMutationResponse>(
    `/api/v1/barriers/${id}`,
    body,
  );
  return response.data;
}

export async function deleteBarrier(
  id: number,
): Promise<BarrierDeletionResponse> {
  const response = await apiClient.delete<BarrierDeletionResponse>(
    `/api/v1/barriers/${id}`,
  );
  return response.data;
}

export async function fetchBarrierGeometry(id: number): Promise<BarrierGeoJsonFeature> {
  const response = await apiClient.get<BarrierGeoJsonFeature>(
    `/api/v1/barriers/${id}/geometry`,
  );
  return response.data;
}

export async function suggestBarrierWaypoints(
  body: SuggestWaypointsRequest,
): Promise<SuggestWaypointsResponse> {
  const response = await apiClient.post<SuggestWaypointsResponse>(
    '/api/v1/barriers/suggest-waypoints',
    body,
  );
  return response.data;
}

export async function listBarriers(
  params: ListBarriersParams,
): Promise<BarriersListResponse> {
  const response = await apiClient.get<BarriersListResponse>('/api/v1/barriers', {
    params: {
      limit: params.limit,
      offset: params.offset,
      period_from: params.periodFrom ?? undefined,
      period_to: params.periodTo ?? undefined,
      radius_min_m: params.radiusMinM ?? undefined,
      radius_max_m: params.radiusMaxM ?? undefined,
      direction: params.direction ?? undefined,
      has_waypoints: params.hasWaypoints ?? undefined,
      search: params.search.length > 0 ? params.search : undefined,
      sort_by: params.sortBy,
      sort_dir: params.sortDir,
    },
    signal: params.signal,
  });
  return response.data;
}

export interface ListBarrierIdsParams extends BarrierListFilters {
  readonly signal?: AbortSignal;
}

export async function listBarrierIds(
  params: ListBarrierIdsParams,
): Promise<BarrierIdsResponse> {
  const response = await apiClient.get<BarrierIdsResponse>('/api/v1/barriers/ids', {
    params: {
      period_from: params.periodFrom ?? undefined,
      period_to: params.periodTo ?? undefined,
      radius_min_m: params.radiusMinM ?? undefined,
      radius_max_m: params.radiusMaxM ?? undefined,
      direction: params.direction ?? undefined,
      has_waypoints: params.hasWaypoints ?? undefined,
      search: params.search.length > 0 ? params.search : undefined,
    },
    signal: params.signal,
  });
  return response.data;
}

export async function bulkDeleteBarriers(
  barrierIds: readonly number[],
): Promise<BulkDeleteBarriersResponse> {
  const response = await apiClient.post<BulkDeleteBarriersResponse>(
    '/api/v1/barriers/bulk/delete',
    { barrier_ids: barrierIds },
  );
  return response.data;
}
